import {
  buildCommandItems,
  type CommandIndexSource,
  type CommandItem,
  type CommandKind,
  conversationSearchGroup,
  filterCommandItems,
  flattenGroups,
  type NavSource,
  SEARCH_LABEL,
  SEARCH_PLACEHOLDER,
  SHELL_NAV_SECTIONS,
  type ShellNavItemDefinition,
} from "@lifter/hub-ui/browser-shared";
import {
  AppWindow,
  ArrowRight,
  BookOpen,
  CornerDownLeft,
  LayoutDashboard,
  LayoutGrid,
  MessagesSquare,
  Package,
  UserRound,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAgentsQuery, useApplicationBlueprintsQuery, useApplicationsQuery } from "../api";
import { SkillCatalogContext, useSkillCatalogQuery } from "../api/skills-org";
import { Button } from "../components/ui/button";
import { Overlay } from "../components/ui/overlay";
import { SearchInput } from "../components/ui/search-input";
import { Inline } from "../components/ui/stack";
import { cn } from "../lib/cn";
import { usePortalOrg } from "../org/context";
import { chromelessAssetPath } from "../routing/chromeless-route";

interface CommandPaletteProps {
  indexItems?: readonly CommandIndexSource[];
  open: boolean;
  onClose(): void;
  onNavigate(path: string): void;
  /** Nav destinations already filtered by role, mirroring the sidebar. */
  navItems: readonly ShellNavItemDefinition[];
}

const KIND_ICON: Record<CommandKind, typeof LayoutGrid> = {
  page: LayoutGrid,
  dashboard: LayoutDashboard,
  application: AppWindow,
  workflow: Workflow,
  blueprint: Package,
  specialist: Users,
  skill: BookOpen,
  member: UserRound,
  "conversation-search": MessagesSquare,
};

/**
 * Top-bar command palette. Searches cached specialists, skills, applications,
 * blueprints, primary nav destinations, and caller-projected workspace index
 * entries, then navigates on select. It indexes no content of its own:
 * conversations and files have no cheap client index, so a typed query is
 * instead offered as a handoff into the conversation rail's server-side text
 * search (see the browser-shared `command-palette.ts`).
 *
 * Reuses the overlay substrate for Escape handling and focus restoration.
 * Result navigation uses the combobox `aria-activedescendant` pattern rather
 * than moving DOM focus per row, so the search input keeps focus while ↑/↓
 * move the active row.
 */
export function CommandPalette({
  indexItems,
  open,
  onClose,
  onNavigate,
  navItems,
}: CommandPaletteProps) {
  const { gatewayOnline } = usePortalOrg();
  // Only fetch the backing catalogs while the palette is open — there is no
  // reason to hold these queries warm for chrome the user may never invoke.
  const agents = useAgentsQuery({ enabled: open && gatewayOnline });
  const applications = useApplicationsQuery({ enabled: open && gatewayOnline });
  const blueprints = useApplicationBlueprintsQuery({ enabled: open && gatewayOnline });
  // Prefer an injected catalog context (tests/stories) over a live query, the
  // same precedence SkillsPage uses. The two shapes only share `data`/`isError`,
  // so loading is normalised below rather than read off either directly.
  const catalogCtx = useContext(SkillCatalogContext);
  const liveCatalog = useSkillCatalogQuery({ enabled: open && catalogCtx === null });
  const catalogData = catalogCtx?.data ?? liveCatalog.data;
  const catalogLoading = catalogCtx ? catalogCtx.isLoading : liveCatalog.isLoading;
  const catalogFailed = catalogCtx ? catalogCtx.isError : liveCatalog.isError;

  /**
   * What each backing catalog actually managed to answer. The palette's verdict
   * on a query is only as good as the sources behind it: a read that rejected
   * searched nothing, and a read that never ran because the runtime is offline
   * searched nothing either — but only the first is a fault worth retrying.
   *
   * The entity catalogs are gated on the runtime; the skill catalog is not, so
   * its state is read independently rather than assumed from `gatewayOnline`.
   */
  const entityState = (query: {
    isError: boolean;
    isLoading: boolean;
    data: unknown;
  }): CatalogSourceState => {
    if (!gatewayOnline) return "disabled";
    if (query.isError) return "failed";
    if (query.isLoading) return "loading";
    return "answered";
  };
  const sources: CatalogSource[] = [
    {
      label: "specialists",
      state: entityState(agents),
      cached: agents.data !== undefined,
      refetch: agents.refetch,
    },
    {
      label: "applications",
      state: entityState(applications),
      cached: applications.data !== undefined,
      refetch: applications.refetch,
    },
    {
      label: "blueprints",
      state: entityState(blueprints),
      cached: blueprints.data !== undefined,
      refetch: blueprints.refetch,
    },
    {
      label: "skills",
      // Deliberately not gated on the runtime: this query runs whenever the
      // palette is open, so declaring it offline-unavailable would misreport a
      // catalog that answered.
      state: catalogFailed ? "failed" : catalogLoading ? "loading" : "answered",
      cached: catalogData !== undefined,
      // An injected catalog (stories, tests) owns its own refresh; only the
      // live query can be retried from here.
      refetch: catalogCtx ? undefined : liveCatalog.refetch,
    },
  ];
  const failedSources = sources.filter((source) => source.state === "failed");
  const disabledSources = sources.filter((source) => source.state === "disabled");
  const retryFailedSources = () => {
    // Only a fault is retried. A source the runtime disabled has nothing to
    // re-attempt, and offering it a retry would invite a click that does
    // nothing.
    for (const source of failedSources) source.refetch?.();
  };

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const listboxId = useId();

  const navSources: NavSource[] = useMemo(
    () =>
      navItems.map((item) => ({
        id: item.id,
        label: item.label,
        href: item.href,
        // The rail section label is the page's result category. The primary
        // ("workspace") section has no heading, so its pages carry no category
        // line rather than repeating the ambiguous raw section id "workspace".
        section: SHELL_NAV_SECTIONS.find((section) => section.id === item.section)?.label,
      })),
    [navItems],
  );

  const items = useMemo(
    () =>
      buildCommandItems({
        nav: navSources,
        agents: agents.data?.agents ?? [],
        index: [
          ...(applications.data?.internal ?? []).map((application) => ({
            id: application.appId,
            kind: "application" as const,
            label: application.title,
            hint: "Internal application",
            path: chromelessAssetPath("application", application.appId),
          })),
          ...(applications.data?.external ?? []).map((application) => ({
            id: application.id,
            kind: "application" as const,
            label: application.title,
            hint: "External application",
            path: `/applications/external/${encodeURIComponent(application.id)}`,
          })),
          ...(blueprints.data?.blueprints ?? []).map((blueprint) => ({
            id: blueprint.id,
            kind: "blueprint" as const,
            label: blueprint.title,
            hint: blueprint.description || "Application blueprint",
            path: `/applications/blueprints/${encodeURIComponent(blueprint.id)}`,
          })),
          ...(indexItems ?? []),
        ],
        skills: [
          ...(catalogData?.bundled ?? []).map((skill) => ({
            id: skill.id,
            title: skill.title,
            description: skill.description,
            source: "bundled" as const,
          })),
          ...(catalogData?.catalog ?? []).map((skill) => ({
            id: skill.id,
            title: skill.title,
            description: skill.description,
            source: "catalog" as const,
          })),
        ],
      }),
    [navSources, agents.data, applications.data, blueprints.data, catalogData, indexItems],
  );

  // Two tiers: the scored index groups, then the conversation handoff. The
  // handoff is appended rather than ranked, so it never displaces a navigable
  // match, and the index's own "no matches" verdict below stays truthful about
  // the index rather than being hidden by an action that always matches.
  const indexGroups = useMemo(() => filterCommandItems(items, query), [items, query]);
  const handoff = useMemo(() => conversationSearchGroup(query), [query]);
  const groups = useMemo(
    () => (handoff ? [...indexGroups, handoff] : indexGroups),
    [handoff, indexGroups],
  );
  const flat = useMemo(() => flattenGroups(groups), [groups]);

  // Reset transient state every time the palette opens so it never reopens onto
  // a stale query or a now-out-of-range active row.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Keep the active row in range as the result set shrinks while typing.
  useEffect(() => {
    setActiveIndex((current) => (current >= flat.length ? 0 : current));
  }, [flat.length]);

  // Scroll the active row into view as the selection moves by keyboard.
  useEffect(() => {
    const active = flat[activeIndex];
    if (!active) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-id="${cssEscape(active.id)}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, flat]);

  if (!open) return null;

  const select = (item: CommandItem | undefined) => {
    if (!item) return;
    onClose();
    onNavigate(item.path);
  };

  // Arrows own result selection; Tab is deliberately left alone so the
  // overlay's focus trap can move through the dialog's controls in both
  // directions. Claiming Tab here made every Tab advance the selection —
  // Shift included, since the modifier was never read — which is neither a
  // combobox nor a focus order.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (flat.length === 0 ? 0 : (current + 1) % flat.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        flat.length === 0 ? 0 : (current - 1 + flat.length) % flat.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      select(flat[activeIndex]);
    }
  };

  // Entity catalogs only load while online; treat offline/idle queries as "not
  // loading" so the empty state reads as "type to search" rather than a stall.
  const loading =
    (gatewayOnline && (agents.isLoading || applications.isLoading || blueprints.isLoading)) ||
    catalogLoading;
  const trimmed = query.trim();
  const activeDescendantId = flat[activeIndex] ? `${listboxId}-${flat[activeIndex].id}` : undefined;

  return (
    <Overlay
      ariaLabelledBy={titleId}
      backdropClassName="absolute inset-0 bg-[var(--color-overlay)]"
      backdropLabel="Close search"
      onClose={onClose}
      open={open}
      rootClassName="fixed inset-0 z-[70] isolate flex items-start justify-center px-4 pt-[12vh]"
      surfaceClassName="relative flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)] outline-none"
    >
      <h2 className="sr-only" id={titleId}>
        Search
      </h2>
      <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-2">
        <SearchInput
          aria-activedescendant={activeDescendantId}
          aria-autocomplete="list"
          aria-controls={listboxId}
          // Reflect whether the listbox actually has options to choose from;
          // a bare `aria-expanded` reads as permanently expanded even when the
          // result set is empty.
          aria-expanded={groups.length > 0}
          data-autofocus="true"
          label={SEARCH_LABEL}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={SEARCH_PLACEHOLDER}
          role="combobox"
          value={query}
          wrapperClassName="min-w-0 flex-1"
        />
        {/* A hint, not a control: it is hidden at the narrow widths where a
            pointer user has no Escape key to press, so the button beside it is
            what has to carry dismissal. */}
        <kbd className="hidden rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-page-bg)] px-1.5 py-0.5 text-[length:var(--type-micro)] font-extrabold uppercase tracking-[var(--type-tracking-micro)] text-[var(--color-text-faint)] sm:inline">
          Esc
        </kbd>
        <Button
          aria-label="Close search"
          data-overlay-close="true"
          iconOnly
          onClick={onClose}
          variant="ghost"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      </div>

      {/* `overscroll-contain` stops a fling at the listbox's scroll bounds
            from chaining into the (locked) document behind the palette. */}
      <div
        aria-label="Search results"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2"
        id={listboxId}
        ref={listRef}
        role="listbox"
      >
        {failedSources.length > 0 ? (
          <CatalogUnavailableNotice onRetry={retryFailedSources} sources={failedSources} />
        ) : null}
        {indexGroups.length === 0 ? (
          <EmptyResults
            disabledSources={disabledSources}
            failedSources={failedSources}
            loading={loading}
            query={trimmed}
          />
        ) : null}
        {groups.map((group) => {
          const Icon = KIND_ICON[group.kind];
          const groupLabelId = `${listboxId}-group-${group.kind}`;
          return (
            // biome-ignore lint/a11y/useSemanticElements: a <fieldset> is a form-grouping element; inside a listbox the ARIA option-group role is the correct semantics.
            <div
              aria-labelledby={groupLabelId}
              className="mb-2 last:mb-0"
              key={group.kind}
              role="group"
            >
              <div
                className="px-2 pb-1 pt-2 text-[length:var(--type-micro)] font-extrabold uppercase tracking-[var(--type-tracking-eyebrow)] text-[var(--color-text-faint)]"
                id={groupLabelId}
                role="presentation"
              >
                {group.label}
              </div>
              {group.items.map((item) => {
                const index = flat.indexOf(item);
                const active = index === activeIndex;
                return (
                  <button
                    aria-selected={active}
                    className={cn(
                      "cursor-pointer flex w-full items-center gap-3 rounded-[var(--radius-md)] px-2 py-2 text-left transition-colors duration-75",
                      active
                        ? "bg-[var(--brand-soft)] text-[var(--brand-dark)]"
                        : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]",
                    )}
                    data-id={item.id}
                    id={`${listboxId}-${item.id}`}
                    key={item.id}
                    onClick={() => select(item)}
                    onMouseMove={() => setActiveIndex(index)}
                    role="option"
                    tabIndex={-1}
                    type="button"
                  >
                    <Icon
                      aria-hidden="true"
                      className="size-4 shrink-0 opacity-80"
                      strokeWidth={1.6}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[length:var(--type-body)] font-bold text-[var(--color-text)]">
                        {item.label}
                      </span>
                      {item.hint ? (
                        <span className="block truncate text-[length:var(--type-meta)] text-[var(--color-text-faint)]">
                          {item.hint}
                        </span>
                      ) : null}
                    </span>
                    {active ? (
                      <ArrowRight
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-[var(--brand-dark)]"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <p aria-live="polite" className="sr-only" role="status">
        {trimmed.length === 0 ? "" : `${flat.length} ${flat.length === 1 ? "result" : "results"}`}
      </p>

      <div className="flex items-center gap-4 border-t border-[var(--color-border)] px-4 py-2 text-[length:var(--type-eyebrow)] font-bold text-[var(--color-text-faint)]">
        <span className="inline-flex items-center gap-1">
          <KeyHint>↑</KeyHint>
          <KeyHint>↓</KeyHint>
          to navigate
        </span>
        <span className="inline-flex items-center gap-1">
          <KeyHint>
            <CornerDownLeft aria-hidden="true" className="size-2.5" />
          </KeyHint>
          to open
        </span>
      </div>
    </Overlay>
  );
}

/** Names the second live region so it is distinguishable from the result count. */
export const CATALOG_NOTICE_LABEL = "Catalogue availability";

type CatalogSourceState = "answered" | "failed" | "loading" | "disabled";

interface CatalogSource {
  label: string;
  state: CatalogSourceState;
  /** A previous read left usable rows behind, so this is a stale refresh. */
  cached: boolean;
  refetch?: () => unknown;
}

function listSources(sources: readonly CatalogSource[]): string {
  const labels = sources.map((source) => source.label);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * What the notice says about a set of failed sources. Every failed source that
 * still has rows on screen is named as saved, whatever its neighbours did: a
 * mixed failure where one source kept its cache and another never had one used
 * to describe both as simply unloadable, leaving visible stale rows presented
 * as current.
 */
export function catalogUnavailableMessage(sources: readonly CatalogSource[]): string {
  const stale = sources.filter((source) => source.cached);
  const lost = sources.filter((source) => !source.cached);
  const staleClause =
    stale.length > 0 ? `Showing saved ${listSources(stale)}, which may be out of date.` : undefined;
  const lostClause =
    lost.length > 0
      ? `Could not load ${listSources(lost)}, so results may be incomplete.`
      : undefined;
  return [lostClause, staleClause].filter(Boolean).join(" ");
}

/**
 * A compact statement that part of the workspace could not be read, sitting
 * above whatever did load rather than replacing it. Navigation, the catalogs
 * that answered, and the conversation handoff all stay usable — the palette is
 * still the fastest way to the thing the user wanted, and a page-sized error
 * would take that away to report a partial fault.
 *
 * Named, because the palette already publishes a result-count live region: an
 * unnamed second status region is indistinguishable from the first to anyone
 * listening, whether that is a screen reader or a test.
 *
 * Status, not brand: the fixed warning palette says "something is wrong here"
 * in every tenant's colours, which the accent cannot.
 */
function CatalogUnavailableNotice({
  onRetry,
  sources,
}: {
  onRetry: () => void;
  sources: readonly CatalogSource[];
}) {
  return (
    <div
      aria-label={CATALOG_NOTICE_LABEL}
      className="mb-[var(--space-2)] rounded-[var(--radius-md)] border border-[color-mix(in_srgb,var(--color-warning)_35%,transparent)] bg-[var(--color-warning-soft)] px-[var(--space-3)] py-[var(--space-2)]"
      role="status"
    >
      <Inline gap="related">
        <p className="min-w-0 flex-1 text-[length:var(--type-label)] text-[var(--color-text)]">
          {catalogUnavailableMessage(sources)}
        </p>
        <Button onClick={onRetry} size="sm" type="button" variant="secondary">
          Retry
        </Button>
      </Inline>
    </div>
  );
}

/**
 * The index's verdict on the query, and only the index's. The conversation
 * handoff below it is always offered and always selectable, so a bare "no
 * matches" here would contradict both the row on screen and the result count
 * announced beneath it; naming what was searched keeps the three in agreement.
 *
 * That naming is also what keeps the verdict honest when a source is missing:
 * "nothing matched" is a claim about everything, and a catalog that failed or
 * never ran was not searched at all.
 */
function EmptyResults({
  disabledSources,
  failedSources,
  loading,
  query,
}: {
  disabledSources: readonly CatalogSource[];
  failedSources: readonly CatalogSource[];
  loading: boolean;
  query: string;
}) {
  let message: string;
  if (loading) {
    // Until the catalogs land there are no items to match against, so a typed
    // query would otherwise read as a false "no matches". Show loading instead.
    message = "Loading your workspace…";
  } else if (query.length === 0) {
    message = "Type to search your workspace.";
  } else if (failedSources.length > 0) {
    message = `No matches in the sources that loaded. ${
      failedSources.length === 1 ? "One source" : "Some sources"
    } could not be searched.`;
  } else if (disabledSources.length > 0) {
    // Offline: the nav pages are props and were searched, and so was any
    // catalog the runtime does not gate. Only the sources that never ran are
    // named as unavailable.
    message = `No matches in the sources that were searched. ${listSources(
      disabledSources,
    )} ${disabledSources.length === 1 ? "is" : "are"} unavailable.`;
  } else {
    message = `No pages, applications, specialists or skills match “${query}”.`;
  }
  return (
    <div className="px-3 py-10 text-center text-[length:var(--type-body)] text-[var(--color-text-faint)]">
      {message}
    </div>
  );
}

function KeyHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-4 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-page-bg)] px-1 py-0.5 text-[var(--color-text-faint)]">
      {children}
    </kbd>
  );
}

// `CSS.escape` is widely available but typed as possibly-undefined in older
// lib targets; the manual fallback keeps the attribute selector safe for ids
// that contain `:` (our items use a `kind:id` scheme).
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
